/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Action, NgxsOnInit, Selector, State, StateContext, Store } from '@ngxs/store';
import { distinctUntilChanged, filter, mergeMap, take, tap } from 'rxjs/operators';

import { SearchService } from '../services/search.service';
import {
    ChooseSearch,
    LoadSearchDefinitions,
    LoadSearches,
    ResetSearchPagingOptions,
    SetSearchCriteria,
    SetSearchPagingOptions,
    SetSearchParams,
    SetSearchView
} from './search.actions';
import { DashGisUtil } from '../../shared/dash-gis-util';
import {
    SearchDefinition,
    SearchFormModel,
    SearchListEntry,
    SearchParamModel,
    SearchStateModel,
    SearchViewState
} from './search.model';
import { isEqual } from 'lodash-es';
import { LoggerService } from '../../shared/services/logger.service';
import { AuthState } from '../../../core/security/state/authState.state';
import { PagingOptions } from '../../../types/PagingOptions';
import { RestService } from '../../shared/services/rest.service';
import { Injectable } from '@angular/core';
import { MapConfigState } from '../../map/state/map-config.state';

@State<SearchStateModel>({
    name: 'search',
    defaults: {
        searches: [],
        definitions: [],
        instances: {}
    }
})
@Injectable()
export class SearchState implements NgxsOnInit {
    private defaultTableState: PagingOptions = {
        page: 0,
        limit: 10,
        sortColumn: null,
        sortDirection: 'asc'
    };

    constructor(
        private logger: LoggerService,
        private searchService: SearchService,
        private rest: RestService,
        private store: Store
    ) {}

    ngxsOnInit(ctx: StateContext<any>) {
        this.store
            .select(AuthState.validated)
            .pipe(
                distinctUntilChanged(),
                filter((b) => b),
                tap(() => setTimeout(() => ctx.dispatch(new LoadSearchDefinitions()), 0)),
            ).subscribe();

        this.store.select(AuthState.validated)
            .pipe(
                distinctUntilChanged(),
                filter((b) => b),
                mergeMap(() => this.store.select(MapConfigState.loaded)),
                distinctUntilChanged(),
                filter((b) => b),
                tap(() => setTimeout(() => this.store.dispatch(new LoadSearches()), 0))
            ).subscribe();
    }

    @Action(LoadSearches)
    loadSearches({ getState, setState }) {
        const state = getState();
        // payload = search name
        if (state.searches.length === 0) {
            return this.searchService.getSearches().pipe(
                tap((searches) => {
                    const newState: SearchStateModel = DashGisUtil.clone(getState());
                    newState.searches = searches;
                    setState(newState);
                })
            );
        }
    }

    @Action(ChooseSearch)
    chooseSearch({ getState, setState }: StateContext<SearchStateModel>, action: ChooseSearch) {
        const newState: SearchStateModel = DashGisUtil.clone(getState());
        let instanceState = newState.instances[action.payload.id];
        if (action.payload.reset || !instanceState || !isEqual(instanceState.search, action.payload.search)) {
            const def = newState.definitions.find((d) => d.name === action.payload.search.name);
            instanceState = {
                search: action.payload.search,
                viewState: 'criteria',
                criteria: { fields: DashGisUtil.clone(def.fields) },
                params: { fields: [] },
                pagingOptions: this.defaultTableState
            };
        }
        newState.instances[action.payload.id] = instanceState;
        setState(newState);
    }

    @Action(LoadSearchDefinitions)
    loadSearchDefinitions({ getState, setState }) {
        return this.rest.apiGet<SearchDefinition[]>('search/definitions').pipe(
            tap((definitions) => {
                const newState: SearchStateModel = DashGisUtil.clone(getState());
                newState.definitions = definitions;
                setState(newState);
            })
        );
    }

    @Action(SetSearchCriteria)
    setSearchCriteria({ getState, setState }: StateContext<SearchStateModel>, action: SetSearchCriteria) {
        const state = DashGisUtil.clone(getState());
        const instance = state.instances[action.payload.instanceId];

        const currentCriteria = instance.criteria;
        action.payload.criteria.fields.forEach((field) => {
            const index = currentCriteria.fields.findIndex(
                (currentField) => currentField.fieldValue.name === field.fieldValue.name
            );
            if (index === -1) {
                currentCriteria.fields.push(field);
            } else {
                currentCriteria.fields.splice(index, 1, field);
            }
        });
        instance.criteria = currentCriteria;
        state.instances[action.payload.instanceId] = instance;
        setState(state);
    }

    @Action(SetSearchParams)
    setSearchParams({ getState, setState }: StateContext<SearchStateModel>, action: SetSearchParams) {
        const state = DashGisUtil.clone(getState());
        const instance = state.instances[action.payload.instanceId];

        instance.params = action.payload.params;
        state.instances[action.payload.instanceId] = instance;
        setState(state);
    }

    @Action(SetSearchView)
    setSearchView({ getState, setState }: StateContext<SearchStateModel>, action: SetSearchView) {
        const state = DashGisUtil.clone(getState());
        const instance = state.instances[action.payload.instanceId];
        instance.viewState = action.payload.view;
        state.instances[action.payload.instanceId] = instance;
        setState(state);
    }

    @Action(SetSearchPagingOptions)
    setPagingOptions({ getState, setState }: StateContext<SearchStateModel>, action: SetSearchPagingOptions) {
        const state = DashGisUtil.clone(getState());
        const id = action.payload.id;
        const instance = state.instances[action.payload.id];
        const currentPaging = instance.pagingOptions;
        if (action.payload.pagingOptions === null) {
            instance.pagingOptions = null;
        } else {
            instance.pagingOptions = Object.assign({}, currentPaging, action.payload.pagingOptions);
        }
        state.instances[id] = instance;
        setState(state);
    }

    @Action(ResetSearchPagingOptions)
    resetPagingOptions({ getState, setState }: StateContext<SearchStateModel>, action: ResetSearchPagingOptions) {
        const state = DashGisUtil.clone(getState());
        const id = action.payload.id;
        const instance = state.instances[action.payload.id];
        instance.pagingOptions = this.defaultTableState;
        state.instances[id] = instance;
        setState(state);
    }

    @Selector()
    static searches(state): SearchListEntry[] {
        return state.searches;
    }

    @Selector()
    static currentSearch(state: SearchStateModel): (string) => SearchListEntry {
        return (instanceId) => {
            const instance = state.instances[instanceId];
            if (instance) {
                return state.instances[instanceId].search;
            }
            return null;
        };
    }

    @Selector()
    static currentCriteria(state: SearchStateModel): (string) => SearchFormModel {
        return (instanceId) => {
            const instance = state.instances[instanceId];
            if (instance) {
                return state.instances[instanceId].criteria;
            }
            return null;
        };
    }

    @Selector()
    static currentParams(state: SearchStateModel): (string) => SearchParamModel {
        return (instanceId) => {
            const instance = state.instances[instanceId];
            if (instance) {
                return state.instances[instanceId].params;
            }
            return null;
        };
    }

    @Selector()
    static currentView(state: SearchStateModel): (string) => SearchViewState {
        return (instanceId) => {
            const instance = state.instances[instanceId];
            if (instance) {
                return state.instances[instanceId].viewState;
            }
            return null;
        };
    }

    @Selector()
    static definitions(state): SearchDefinition[] {
        return state.definitions;
    }

    @Selector()
    static currentDefinition(state: SearchStateModel): (string) => SearchDefinition {
        return (instanceId) => {
            const instance = state.instances[instanceId];
            if (instance) {
                const searchName = state.instances[instanceId].search.name;
                return state.definitions.find((d) => d.name === searchName);
            }
            return null;
        };
    }

    @Selector()
    static paging(state): (string) => PagingOptions {
        return (instanceId) => {
            const instance = state.instances[instanceId];
            if (instance) {
                return instance.pagingOptions;
            }
            return null;
        };
    }

    @Selector()
    static previousSearchAvailable(state: SearchStateModel): (string) => boolean {
        return (instanceId) => {
            const instance = state.instances[instanceId];
            if (!instance) {
                return false;
            }
            const def = state.definitions.find((d) => d.name === instance.search.name);
            return !isEqual(instance.criteria.fields, def.fields);
        };
    }
}
